import { Button, Callout, ExternalLink } from "@octopusdeploy/design-system-components";
import { useInlineStatusMutation, useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import type { ProjectResource } from "@octopusdeploy/octopus-server-client";
import React, { useState } from "react";
import DeleteDialogLayout from "~/components/Dialog/DeleteDialogLayout";
import Dialog from "~/components/Dialog/Dialog";
import OkDialogLayout from "~/components/DialogLayout/OkDialogLayout";
import { Text } from "~/components/form/index";
interface RotateClientIdentifierDialogProps {
    project: ProjectResource;
}
interface ManageClientIdentifierState {
    isRotationPromptDialogOpen: boolean;
    rotationConfirmationValue: string;
}
export function RotateClientIdentifierDialog({ project }: RotateClientIdentifierDialogProps) {
    const [state, setState] = useState<ManageClientIdentifierState>({
        isRotationPromptDialogOpen: false,
        rotationConfirmationValue: "",
    });
    const { error: getRotationStateError, isLoading: getRotationStateBusy, result: rotationState, refetch: refetchRotationState, } = useInlineStatusQuery(async (repository) => {
        const rotationState = await repository.FeatureToggles.getClientIdentifierRotationState(project);
        return rotationState.RotationState;
    }, [project], "Get Client Identifier Rotation State");
    const showRotationPromptDialog = () => {
        setState((prevState) => ({ ...prevState, isRotationPromptDialogOpen: true }));
    };
    const confirmationOnChange = (value: string) => {
        setState((prevState) => ({ ...prevState, rotationConfirmationValue: value }));
    };
    const { error: rotateClientIdentifiersError, execute: rotateClientIdentifiers, isExecuting: rotateClientIdentifiersBusy, } = useInlineStatusMutation({
        name: "Add Feature Toggle",
        action: async (repository) => {
            const completeRotation = rotationState === "Rotating";
            await repository.FeatureToggles.rotateClientIdentifierSigningKey(project, completeRotation);
        },
        afterComplete: async () => {
            refetchRotationState();
        },
    });
    const rotationDialog = (label: string, prompt: string) => {
        return (<>
                <Button label={label} onClick={() => showRotationPromptDialog()} importance="destructive"/>
                <Dialog open={state.isRotationPromptDialogOpen}>
                    <DeleteDialogLayout title={prompt} onClose={() => {
                setState((prevState) => ({ ...prevState, rotationConfirmationValue: "", isRotationPromptDialogOpen: false }));
            }} deleteButtonDisabled={state.rotationConfirmationValue !== "Rotate"} onDeleteClick={() => rotateClientIdentifiers()} deleteButtonLabel={label} renderContent={() => (<>
                                <Callout title="This is a destructive action" type={"danger"}>
                                    This action <strong>cannot</strong> be undone.
                                </Callout>
                                <div>
                                    <p>
                                        Please type in <strong>Rotate</strong> to confirm.
                                    </p>
                                    <Text value={state.rotationConfirmationValue} onChange={confirmationOnChange}/>
                                </div>
                            </>)}/>
                </Dialog>
            </>);
    };
    function getErrors() {
        const errors = [];
        if (rotateClientIdentifiersError) {
            errors.push(rotateClientIdentifiersError);
        }
        if (getRotationStateError) {
            errors.push(getRotationStateError);
        }
        return errors;
    }
    return (<OkDialogLayout title={"Rotate Client Identifiers"} busy={rotateClientIdentifiersBusy || getRotationStateBusy} errors={getErrors()} onOkClick={() => { }} hideCancel={true}>
            <React.Fragment>
                {rotationState === "Rotating" && (<>
                        <Callout title="This is a destructive action" type={"warning"}>
                            You are part way through rotating your client identifiers. Instructions for performing a safe regeneration are available in our{" "}
                            <ExternalLink href={"https://oc.to/RotatingClientIdentifiers"}>Rotating Client Identifiers</ExternalLink> documentation.
                            <br />
                            <br />
                            Completing the rotation will invalidate your pre-existing client identifiers. Only do this once you have redeployed the newly rotated client identifier through all environments.
                        </Callout>
                        <br />
                        {rotationDialog("Complete Rotation", "Are you sure you want to complete rotating your client identifiers?")}
                    </>)}
                {rotationState === "NotRotating" && (<>
                        <Callout title="This is a destructive action" type={"warning"}>
                            Rotating your client identifiers is a two-step process. Instructions for performing a safe rotation are available in our{" "}
                            <ExternalLink href={"https://oc.to/RotatingClientIdentifiers"}>Rotating Client Identifiers</ExternalLink> documentation.
                            <br />
                            <br />
                            <b>You should only use this function if your client identifier has leaked and you need to rotate it to ensure your feature toggles remain secure.</b>
                        </Callout>
                        <br />
                        {rotationDialog("Rotate", "Are you sure you want to rotate your client identifiers?")}
                    </>)}
            </React.Fragment>
        </OkDialogLayout>);
}
